import type { MockFeedback } from './types';

// Generatore deterministico di feedback realistici per 25 atleti × 30 giorni.
// Pattern: ogni atleta ha una "personalità" fissa (responsabile / sporadico / latitante) e
// un "ciclo settimanale" di fatica (lunedì basso → venerdì alto → weekend recupero).

const TODAY = new Date('2026-04-22T12:00:00');

type Personality = 'attivo' | 'regolare' | 'sporadico' | 'latitante';

const PERSONALITIES: Personality[] = [
    'attivo',
    'regolare',
    'regolare',
    'sporadico',
    'attivo',
    'regolare',
    'regolare',
    'sporadico',
    'regolare',
    'attivo',
    'sporadico',
    'latitante',
    'attivo',
    'regolare',
    'regolare',
    'sporadico',
    'regolare',
    'attivo',
    'sporadico',
    'regolare',
    'attivo',
    'regolare',
    'latitante',
    'sporadico',
    'regolare'
];

// Probabilità di feedback in un giorno dato la personalità
const P_FEEDBACK: Record<Personality, number> = {
    attivo: 0.95,
    regolare: 0.75,
    sporadico: 0.4,
    latitante: 0.15
};

// Baseline fatica (1-5) per personalità
const FATIGUE_BASE: Record<Personality, number> = {
    attivo: 2.2,
    regolare: 2.8,
    sporadico: 3.2,
    latitante: 3.8
};

// Hash deterministico → pseudo-random
function hashSeed(seed: string): number {
    let h = 2166136261;
    for (let i = 0; i < seed.length; i++) {
        h ^= seed.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return (h >>> 0) / 4294967295;
}

function clamp(v: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, v));
}

function fmtDate(d: Date): string {
    return d.toISOString().slice(0, 10);
}

// Pool note realistiche per mood/fatica
const NOTES_POS = [
    'Sensazioni ottime, nuotata fluida',
    'Bene oggi, braccia leggere',
    'Gara simulata perfetta',
    'Ho tenuto bene il passaggio sui 50',
    'Vasca molto produttiva',
    'Respirazione a posto, passo costante',
    'Partenze reattive',
    'Buon ritmo sul lungo'
];

const NOTES_NEU = [
    'Tutto ok, nulla da segnalare',
    'Normale',
    'Ho completato la seduta',
    'Carico ok',
    'Giornata regolare'
];

const NOTES_NEG = [
    'Gambe pesanti, ho faticato sul lungo',
    "Braccio destro un po' dolorante",
    'Non riuscivo a spingere sui 100',
    'Respirazione difficile, mai in ritmo',
    'Dopo le ripetute ero esausto',
    'Serata pesante, meglio recuperare',
    'Spalla sinistra che tira',
    'Troppo stanco, forse virus in arrivo',
    'Scarse sensazioni, poca energia'
];

function pickNote(seed: string, mood: 'pos' | 'neu' | 'neg', noNotePct: number): string | undefined {
    const r = hashSeed(seed + 'note');
    if (r < noNotePct) return undefined;
    const pool = mood === 'pos' ? NOTES_POS : mood === 'neg' ? NOTES_NEG : NOTES_NEU;
    const rr = hashSeed(seed + 'pick');
    return pool[Math.floor(rr * pool.length)];
}

// Genera feedback per un atleta
function generateForAthlete(athleteId: string, personalityIdx: number): MockFeedback[] {
    const personality = PERSONALITIES[personalityIdx % PERSONALITIES.length];
    const pFeedback = P_FEEDBACK[personality];
    const fatigueBase = FATIGUE_BASE[personality];
    const out: MockFeedback[] = [];

    for (let d = 0; d < 30; d++) {
        const date = new Date(TODAY);
        date.setDate(date.getDate() - d);
        const dateStr = fmtDate(date);
        const dow = date.getDay(); // 0=Dom, 1=Lun, ... 6=Sab

        // Skip weekend per alcuni (domenica: 30% chi riposa)
        if (dow === 0 && hashSeed(athleteId + dateStr + 'sun') < 0.3) continue;

        // Decide se atleta ha fatto feedback
        const seedDay = athleteId + dateStr;
        if (hashSeed(seedDay + 'do') > pFeedback) continue;

        // Fatica: picco mercoledì-giovedì (dow 3-4), minima domenica (dow 0)
        const weekCycle = [0, 1, 2, 3.5, 3.5, 2.5, 1.5][dow]; // contributo settimanale
        const dailyNoise = (hashSeed(seedDay + 'noise') - 0.5) * 1.5;
        const fatigue = clamp(Math.round(fatigueBase + weekCycle * 0.4 + dailyNoise), 1, 5);

        // Velocità: inversamente correlata a fatica
        const speedSeed = hashSeed(seedDay + 'spd');
        const speed = clamp(Math.round(5 - (fatigue - 3) * 0.6 + (speedSeed - 0.5) * 2), 1, 5);

        // Resistenza: meno variabile, segue fatica
        const endSeed = hashSeed(seedDay + 'end');
        const endurance = clamp(Math.round(4.5 - (fatigue - 3) * 0.5 + (endSeed - 0.5) * 1.5), 1, 5);

        // Determina mood (per note)
        const avgVR = (speed + endurance) / 2;
        const mood: 'pos' | 'neu' | 'neg' =
            avgVR >= 3.5 && fatigue <= 3 ? 'pos' : fatigue >= 4 && avgVR <= 2.5 ? 'neg' : 'neu';

        // Note (50% dei feedback hanno note)
        const notes = pickNote(seedDay, mood, 0.5);

        // Private feedback ogni 4-5 record
        const hasPrivate = hashSeed(seedDay + 'priv') < 0.25;
        const privateData = hasPrivate
            ? {
                  intensity: clamp(fatigue + Math.round((hashSeed(seedDay + 'int') - 0.5) * 2), 1, 5),
                  pushed: clamp(5 - fatigue + Math.round((hashSeed(seedDay + 'push') - 0.5) * 2), 1, 5),
                  motivation: clamp(Math.round(avgVR + (hashSeed(seedDay + 'mot') - 0.5) * 2), 1, 5),
                  fun: clamp(3 + Math.round((hashSeed(seedDay + 'fun') - 0.5) * 3), 1, 5),
                  fatigueManagement: clamp(5 - fatigue + Math.round((hashSeed(seedDay + 'fmgm') - 0.5) * 2), 1, 5),
                  sessionUsefulness: clamp(3 + Math.round((hashSeed(seedDay + 'use') - 0.5) * 3), 1, 5)
              }
            : undefined;

        out.push({
            id: `fb-${athleteId}-${dateStr}`,
            athleteId,
            workoutId: `wk-${(d % 15) + 1}`,
            date: dateStr,
            public: { speed, endurance, muscleFatigue: fatigue },
            private: privateData,
            notes
        });
    }

    return out;
}

// Atleti: 25 regolari (athlete-1..athlete-25) + atleta self
const ATHLETE_IDS = ['athlete-self', ...Array.from({ length: 25 }, (_, i) => `athlete-${i + 1}`)];

export const mockFeedback: MockFeedback[] = ATHLETE_IDS.flatMap((id, i) => generateForAthlete(id, i));
